// Get the API URL from environment variables
console.log('🌐 Environment check:', {
  hostname: window.location.hostname,
  protocol: window.location.protocol,
  port: window.location.port
});

const getApiUrl = (): string => {
  // Check if we have a configured API URL from the build
  if (typeof window !== 'undefined' && (window as any).__API_URL) {
    return `https://${(window as any).__API_URL}/api/v1`;
  }

  // Fallback based on current hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'app.finderz.ai' || hostname.includes('app.finderz.ai')) {
      return 'https://api.finderz.ai/api/v1';
    }
    else if (hostname === 'preprod.finderz.ai' || hostname.includes('preprod.finderz.ai')) {
      return 'https://api.preprod.finderz.ai/api/v1';
    }
    else if (hostname === 'app.stage.finderz.dev' || hostname.includes('stage.finderz.dev')) {
      return 'https://api.stage.finderz.dev/api/v1';
    } else if (hostname === 'app.finderz.dev' || hostname.includes('finderz.dev')) {
      return 'https://api.finderz.dev/api/v1';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000/api/v1';
    }
  }

  // Default fallback - use finderz.dev for production calls
  return 'https://api.finderz.dev/api/v1';
};

export const API_URL = getApiUrl();

// API Error handler for improved error handling
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public response?: any,
    message?: string
  ) {
    super(message || `HTTP ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

// Centralized API request handler with automatic token refresh
export const apiRequest = async (
  url: string,
  options: RequestInit = {},
  keycloak?: any
): Promise<Response> => {
  try {
    // Ensure headers exist
    options.headers = options.headers || {};

    // Make the initial request
    let response = await fetch(url, options);

    // Handle different HTTP status codes
    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch (e) {
        // Could not read error response body
      }

      // Special handling for different error types
      if (response.status === 401) {
        console.error('❌ 401 Unauthorized - token may be expired');
        if (keycloak) {
          console.log('🔄 Attempting token refresh for 401...');
          try {
            // Force token refresh (0 means refresh if expired, -1 means force refresh)
            const refreshed = await keycloak.updateToken(0);
            console.log('🔄 Token refresh result:', refreshed);

            if (refreshed || keycloak.token) {
              console.log('✅ Token refreshed, retrying request...');
              console.log('🔗 New token (first 20 chars):', keycloak.token?.substring(0, 20) + '...');

              // Retry request with new token
              options.headers = {
                ...options.headers,
                'authorization': `Bearer ${keycloak.token}`
              };
              response = await fetch(url, options);

              console.log('🔄 Retry response status:', response.status, response.ok);

              // Check if retry was successful
              if (response.ok) {
                console.log('✅ Retry successful after token refresh');
                return response;
              } else {
                // Even after refresh, still getting error
                let retryErrorBody = '';
                try {
                  retryErrorBody = await response.text();
                } catch (e) {}
                console.error('❌ Retry failed even after token refresh:', response.status, retryErrorBody);
                throw new ApiError(response.status, response.statusText, retryErrorBody, 'Request failed even after token refresh');
              }
            } else {
              console.error('❌ Token refresh did not provide new token');
              keycloak.login();
              throw new ApiError(401, 'Unauthorized', errorBody, 'Token refresh failed - please log in again');
            }
          } catch (refreshError) {
            console.error('❌ Token refresh exception:', refreshError);
            // Handle specific token refresh errors
            if (refreshError instanceof Error) {
              console.error('❌ Token refresh error details:', refreshError.message);
            }

            // Don't throw unhandled rejection, handle gracefully
            try {
            keycloak.login();
            } catch (loginError) {
              console.error('❌ Login redirect failed:', loginError);
            }

            throw new ApiError(401, 'Unauthorized', errorBody, 'Session expired, please log in again');
          }
        }
        throw new ApiError(401, 'Unauthorized', errorBody, 'Authentication required');
      }

      if (response.status === 403) {
        throw new ApiError(403, 'Forbidden', errorBody, 'You do not have permission to access this resource');
      }

      if (response.status === 502) {
        throw new ApiError(502, 'Bad Gateway', errorBody, 'Server temporarily unavailable. Please try again in a few moments.');
      }

      if (response.status === 503) {
        throw new ApiError(503, 'Service Unavailable', errorBody, 'Service temporarily unavailable. Please try again later.');
      }

      if (response.status === 504) {
        throw new ApiError(504, 'Gateway Timeout', errorBody, 'Request timed out. Please try again.');
      }

      if (response.status >= 500) {
        throw new ApiError(response.status, response.statusText, errorBody, 'Server error occurred. Please try again later.');
      }

      // For other client errors (4xx)
      throw new ApiError(response.status, response.statusText, errorBody);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network errors, etc.
    console.error('Network or unexpected error:', error);
    throw new Error('Network error. Please check your connection and try again.');
  }
};

// Helper function to safely parse JSON responses
export const safeJsonParse = async (response: Response): Promise<any> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('Failed to parse JSON response:', text);
    throw new Error('Invalid JSON response from server');
  }
};

console.log('✅ API configuration loaded:', API_URL);
